import { useCurrentUserData } from '@framework/auth';
import {
  IParseThemeValidationContextData,
  IThemeValidationContext,
  IUseThemeValidationContext,
} from '@framework/theme/src/engine/useThemeValidationContext';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import {
  DatiSiapDTO,
  RapportoComandoDTO,
  RapportoIstitutoDTO,
} from '../../api/employee/schemas';

export const THEME_SIAP_KEY = 'siap';

function convertUnixTimestampToDate(
  timestamp: number | string | null | undefined,
) {
  // È già in formato ISO, non facciamo nulla se non adattare il formato.
  if (typeof timestamp === 'string' && timestamp.trim() !== '') {
    return dayjs(timestamp, [
      'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]',
      'YYYY-MM-DD[T]HH:mm:ss',
      'YYYY-MM-DD[T]HH:mm',
      'YYYY-MM-DD',
    ]).format('YYYY-MM-DD');
  }

  // Timestamp in millisecondi.
  if (typeof timestamp === 'number' && !isNaN(timestamp)) {
    // Non possiamo usare [T]HH:mm:ss perché i timestamp ritornati sono in UTC "errati" (es. 1 di notte)
    return dayjs(timestamp).format('YYYY-MM-DD');
  }

  // Ignoriamo le date nulle o con timestamp 0, non sono valide.
  return null;
}

function parseUserDataRapportoIstituto(
  rapporto: RapportoIstitutoDTO | null | undefined,
) {
  if (!rapporto) {
    return null;
  }

  return {
    ...rapporto,
    dataInizio: convertUnixTimestampToDate(rapporto.dataInizio),
    dataPresaServizio: convertUnixTimestampToDate(rapporto.dataPresaServizio),
    dataFine: convertUnixTimestampToDate(rapporto.dataFine),
  };
}

function parseUserDataRapportoComando(
  rapporto: RapportoComandoDTO | null | undefined,
) {
  if (!rapporto) {
    return null;
  }

  return {
    ...rapporto,
    dataInizio: convertUnixTimestampToDate(rapporto.dataInizio),
    dataFine: convertUnixTimestampToDate(rapporto.dataFine),
  };
}

function parseUserCustomAttributes(
  user: IParseThemeValidationContextData | null | undefined,
) {
  const siap = user?.custom as DatiSiapDTO | undefined;

  return {
    anagrafica: {
      ...siap?.anagrafica,
      dataNascita: convertUnixTimestampToDate(siap?.anagrafica?.dataNascita),
    },
    assunzione: {
      ...siap?.assunzione,
      dataInizio: convertUnixTimestampToDate(siap?.assunzione?.dataInizio),
      dataFine: convertUnixTimestampToDate(siap?.assunzione?.dataFine),
    },
    profilo: {
      ...siap?.profilo,
      dataInizio: convertUnixTimestampToDate(siap?.profilo?.dataInizio),
      dataEconomica: convertUnixTimestampToDate(siap?.profilo?.dataEconomica),
      dataFine: convertUnixTimestampToDate(siap?.profilo?.dataFine),
    },
    assegnazione: parseUserDataRapportoIstituto(siap?.assegnazione),
    incaricoDirigenziale: parseUserDataRapportoIstituto(
      siap?.incaricoDirigenziale,
    ),
    distacco: parseUserDataRapportoIstituto(siap?.distacco),
    comandoOut: parseUserDataRapportoComando(siap?.comandoOut),
    cessazione: {
      ...siap?.cessazione,
      dataCessazione: convertUnixTimestampToDate(
        siap?.cessazione?.dataCessazione,
      ),
    },
  };
}

export type ISiapUserData = NonNullable<
  ReturnType<typeof parseUserCustomAttributes>
>;

/**
 * @deprecated Non utilizzato, vedere `useEngineValidationContext` che
 * al suo interno chiama `parseThemeValidationContext`.
 */
export const useThemeValidationContext: IUseThemeValidationContext = () => {
  // Dati utente LDAP/SIAP
  const userData = useCurrentUserData();

  // Inizializziamo i valori in modo tale che gli
  // oggetti siano sempre presenti.
  const value = useMemo(
    () => parseThemeValidationContext(userData),
    [userData],
  );

  return value;
};

/**
 * Funzione che genera la struttura del contesto di validazione per il tema.
 * @param user Dati utente LDAP/SIAP
 */
export function parseThemeValidationContext(
  user: IParseThemeValidationContextData | null | undefined,
): IThemeValidationContext {
  return {
    session: {
      [THEME_SIAP_KEY]: parseUserCustomAttributes(user),
    },
  };
}
